import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  Inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
  viewChild,
  ViewChild,
  viewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthenService } from '@core/authen/authen.service';
import { SubdomainOrgFilterMarker } from '@core/bootstrap/subdomain-org-filter.initializer';
import { ChallengeHttpService } from '@core/http/challenge-http.service';
import { OrganizationHttpService } from '@core/http/organization-http.service';
import { I18nService } from '@core/i18n/i18n.service';
import { SKIP_INNOVATION_SPACE } from '@core/interceptors/api-headers.interceptor';
import { CommunitySelectorStoredService } from '@core/services/community-selector-stored.service';
import { FactoryService } from '@core/services/factory.service';
import { SearchChannelsService } from '@core/services/search-channels.service';
import { UserConfigManagementService } from '@core/services/user-config-management.service';
import { SessionService } from '@core/session.service';
import { ToastService } from '@core/toast.service';
import { environment } from '@env/environment';
import {
  NgbActiveModal,
  NgbDropdown,
  NgbDropdownModule,
  NgbModal,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  matChevronLeft,
  matChevronRight,
  matInfo,
} from '@ng-icons/material-icons/baseline';
import {
  matAddCircleOutline,
  matArrowDropDownOutline,
  matArrowDropUpOutline,
  matDragIndicatorOutline,
  matMoreHorizOutline,
  matReorderOutline,
} from '@ng-icons/material-icons/outline';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { GlobalFilterComponent } from '@shared/components/global-filter/global-filter.component';
import { OrganizationLogoComponent } from '@shared/components/molecules/organization-logo/organization-logo.component';
import { OrganisationSearchComponent } from '@shared/components/organisation-search/organisation-search.component';
import { DebounceClickDirective } from '@shared/directives/debounce-click.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { HttpStatusCodeEnum } from '@shared/enums/httpstatuscode.enum';
import { OrgsSearchMode } from '@shared/enums/org-search-mode.enum';
import { StorageEnum } from '@shared/enums/storage.enum';
import { UrlParam } from '@shared/enums/url-param.enum';
import { WINDOW } from '@shared/helpers/window.token';
import {
  OrganizationFilter,
  OrganizationInterface,
} from '@shared/interfaces/organization.interface';
import { TenantInterface } from '@shared/interfaces/tenant.interface';
import { MediaBreakpoint } from '@shared/models/ui.model';
import { CallPipe } from '@shared/pipes/call.pipe';
import { LinkWrapperPipe } from '@shared/pipes/link-wrapper.pipe';
import { StringReplacementPipe } from '@shared/pipes/string-replacement.pipe';
import { FormUtils } from '@shared/utils/form-utils';
import { StringUtils } from '@shared/utils/string-utils';
import { SubdomainUrlUtils } from '@shared/utils/subdomain-url.utils';
import { isMobile } from 'detect-touch-device';
import { EventBusService } from 'ngx-eventbus';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  firstValueFrom,
  lastValueFrom,
  of,
  pairwise,
  Subscription,
  switchMap,
  take,
  throwError,
} from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-community-selector',
  templateUrl: './community-selector.component.html',
  providers: [NgbActiveModal, NgbDropdown],
  imports: [
    NgIcon,
    CommonModule,
    NgbDropdownModule,
    OrganizationLogoComponent,
    TranslateModule,
    TooltipDirective,
    NgbTooltipModule,
    FormsModule,
    DebounceClickDirective,
    OrganisationSearchComponent,
    StringReplacementPipe,
    LinkWrapperPipe,
    CallPipe,
    DragDropModule,
  ],
  viewProviders: [
    provideIcons({
      matAddCircleOutline,
      matArrowDropDownOutline,
      matArrowDropUpOutline,
      matChevronLeft,
      matChevronRight,
      matDragIndicatorOutline,
      matInfo,
      matMoreHorizOutline,
      matReorderOutline,
    }),
  ],
})
export class CommunitySelectorComponent
  extends GlobalFilterComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  isDropdownMode = input(false);

  mainContent = viewChild('mainContent', { read: ElementRef });

  favoriteOpened = true;
  keyword = '';
  orgsSearchMode = OrgsSearchMode;
  environment = environment;
  smallScreen = false;
  hasTouchInput: boolean = isMobile;
  popularOrgSubscription: Subscription;

  private popularOrgsLoading: Promise<void> = Promise.resolve();

  userPreferences = {
    expanded: false,
    popularOpened: false,
  };
  userPreferencesLoading = false;
  orderModeActivated = false;
  savingOrgOrder = false;

  shouldScrollCommunitySpaces = false;

  private originalOrgList = [];

  private currentSwipeElement: HTMLElement | null = null;
  private mobileRemoveBtnDisplayed = false;
  private activeSwipeItemId: number;
  private activeSwipeDirection: 'left' | 'right';
  private readonly popularOrgs$ = new BehaviorSubject<
    (OrganizationFilter & OrganizationInterface)[] | null
  >(null);

  get ignoreOrgIds(): number[] {
    return this.orgFilterList.map(o => o.id);
  }

  get selectedOrg(): OrganizationFilter {
    const org = super.selectedOrg;

    if (!org) {
      const popularOrg = this.popularOrgs?.find(
        x => x.id === this.organizationId
      );

      if (popularOrg) {
        this.orgUrl = environment.jipUrl.organizations;
        popularOrg.name = popularOrg.orgName;
        popularOrg.imgUrl = popularOrg.logo.url;
        popularOrg.isPopular = true;
      }
      return popularOrg;
    }

    return org;
  }

  get expanded(): boolean {
    return (
      this.smallScreen || this.isDropdownMode() || this.userPreferences.expanded
    );
  }

  get popularOpened(): boolean {
    return this.smallScreen || this.userPreferences.popularOpened;
  }

  @ViewChild('modalRef') modalRef: ElementRef;
  @ViewChild('organizationSearch')
  organizationSearchComp!: OrganisationSearchComponent;

  swipeableOrgItems = viewChildren('swipeableOrgItem', { read: ElementRef });

  orgSelected = output<void>();

  @HostListener('window:resize')
  onResize(): void {
    this.withSmallScreen();
    if (this.smallScreen) {
      document.body.classList.remove('community-selector-collapse');
    } else {
      this.updateBodyClass();
    }
  }

  @HostListener('window:load')
  onLoad(): void {
    this.withSmallScreen();
  }

  private readonly i18Service = inject(I18nService);

  constructor(
    @Inject(WINDOW) protected window: Window,
    public sessionService: SessionService,
    protected eventBus: EventBusService,
    protected authService: AuthenService,
    protected factoryService: FactoryService,
    protected userConfig: UserConfigManagementService,
    protected readonly renderer: Renderer2,
    protected readonly injector: Injector,
    protected searchChannel: SearchChannelsService,
    protected orgHttpService: OrganizationHttpService,
    protected communitySelectorStored: CommunitySelectorStoredService,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService
  ) {
    super(
      window,
      sessionService,
      eventBus,
      authService,
      factoryService,
      userConfig,
      renderer,
      injector,
      searchChannel,
      orgHttpService,
      communitySelectorStored
    );

    this.withSmallScreen();

    effect(() => {
      const items = this.swipeableOrgItems();

      if (items?.length > 0) {
        void this.attachSwipeHandlers(items);
      }
    });
  }

  ngAfterViewInit(): void {
    this.resetInnovationSpaceOnHomePage();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.listenToSearch();

    this.listenToCentralConfig();

    this.resetFavoriteOrgIfTenantChanged();

    this.listenToResetInnovationSpace();

    this.listenWindowScrollEvent();

    this.listenToOrgFilterList();

    void this.getUserPreferences();

    this.listenToSelectedOrg();

    this.listenToOrgInfoUpdated();
  }

  listenToOrgInfoUpdated(): void {
    this.communitySelectorStored.orgInfoUpdated
      .pipe(untilDestroyed(this))
      .subscribe(({ id, slug, name, subdomain }) => {
        const org = this.orgFilterList.find(item => item.id === id);

        if (!org) return;

        org.slug = slug ?? '';
        if (name) org.name = name;
        if (subdomain !== undefined) org.subdomain = subdomain;

        this.orgFilterList$.next(this.orgFilterList);
      });
  }

  ngOnDestroy(): void {
    globalThis.removeEventListener('wheel', this.onWheel.bind(this));
  }

  listenToOrgFilterList(): void {
    this.orgFilterList$
      .pipe(
        switchMap(org => {
          return of(org);
        })
      )
      .subscribe(orgs => {
        this.originalOrgList = structuredClone(orgs);
      });
  }

  listenToSelectedOrg(): void {
    const filterReady$ = this.sessionService.apiReady$.pipe(
      untilDestroyed(this),
      filter(Boolean),
      switchMap(() => this.filterStoredService.filterReady$)
    );

    filterReady$
      .pipe(
        untilDestroyed(this),
        filter(() => !this.centralConfig.innovationSpaceId),
        switchMap(() =>
          this.communitySelectorStored.selectedOrg.pipe(
            untilDestroyed(this),
            pairwise()
          )
        )
      )
      .subscribe(([prevOrg, org]) => {
        if (SubdomainOrgFilterMarker.lastAppliedSubdomain) {
          return;
        }

        if (org) {
          this.organizationSlug = org.slug;

          const value = this.organizationSlug || org.id.toString();

          StringUtils.setQueryParamOnUrl(
            UrlParam.FilterTokenId,
            value,
            null,
            true
          );
        }

        if (prevOrg && !org) {
          this.organizationSlug = '';
          StringUtils.removeParamFromUrl(UrlParam.FilterTokenId, null, true);
        }
      });
  }

  async handleCurrentFilterList(): Promise<void> {
    let currentOrgFilter = await this.userConfig.get(
      StorageEnum.orgFilterToken
    );

    // Not awaited: popular orgs must not block filterReady$ (and so the list API).
    // Started before the lite-org refresh below, which only changes name/logo/slug, not ids.
    const ignoreIds = currentOrgFilter?.map(item => item.id) ?? [];

    this.popularOrgsLoading = this.loadPopularOrg(ignoreIds);

    if (currentOrgFilter) {
      if (
        !this.sessionService.authenticated &&
        !this.centralConfig.innovationSpaceId
      ) {
        const updatedOrganizations =
          await this.updateLatestOrganizationInfo(currentOrgFilter);

        currentOrgFilter = updatedOrganizations;
      }

      this.orgFilterList = this.removeDuplicates(currentOrgFilter);
      this.orgFilterList$.next(this.orgFilterList);
    }
  }

  protected async updateFilterTokenStatus(orgId: number): Promise<void> {
    // Only an org missing from the saved tokens may be found in the popular list.
    if (!this.orgFilterList.some(org => org.id === orgId)) {
      await this.popularOrgsLoading;
    }

    await super.updateFilterTokenStatus(orgId);
  }

  async updateLatestOrganizationInfo(
    organizations: OrganizationFilter[] = []
  ): Promise<OrganizationFilter[]> {
    const ids = organizations.map(o => o.id).filter((id): id is number => !!id);

    if (!ids.length) return [];

    const latestOrganizations = await lastValueFrom(
      this.organizationHttpService.getLiteOrganizations(ids)
    );

    for (const org of organizations) {
      if (!org.id) continue;

      const matchedOrganization = latestOrganizations.find(
        latestOrg => latestOrg.id === org.id
      );

      if (matchedOrganization) {
        org.imgUrl = matchedOrganization.logo?.url;
        org.name = matchedOrganization.orgName;
        org.slug = matchedOrganization.slug ?? '';
        org.subdomain = matchedOrganization.subdomain ?? '';
      }
    }

    await this.userConfig.set(StorageEnum.orgFilterToken, organizations);

    return organizations;
  }

  async removeItem(orgFilter: OrganizationFilter): Promise<void> {
    await super.removeItem(orgFilter);
    await this.loadPopularOrg();
  }

  protected async addFilterTokenFromOutside(orgId: number): Promise<void> {
    const isOnlyPopular =
      orgId > 0 &&
      !this.orgFilterList.some(org => org.id === orgId) &&
      this.popularOrgs.some(org => org.id === orgId);

    if (isOnlyPopular) {
      this.storeNewFavoriteOrg(orgId, true);
    }

    await super.addFilterTokenFromOutside(orgId);
  }

  listenWindowScrollEvent() {
    globalThis.addEventListener('wheel', this.onWheel.bind(this), {
      passive: false,
    });
  }

  onWheel(event: WheelEvent) {
    const element = this.mainContent()?.nativeElement;

    if (!this.shouldScrollCommunitySpaces || !element) return;

    const delta = event.deltaY;

    event.preventDefault();
    element.scrollTop += delta;
  }

  async getUserPreferences(): Promise<void> {
    this.userPreferencesLoading = true;

    const userPreferences = await this.userConfig.get(
      StorageEnum.communitySelectorPreferences
    );

    this.userPreferencesLoading = false;

    if (userPreferences) {
      this.userPreferences = structuredClone(userPreferences);
      this.updateBodyClass();
    }
  }

  resetFavoriteOrgIfTenantChanged() {
    combineLatest([
      this.authService.authenticated$,
      this.sessionService.tenant$,
      this.orgFilterList$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([authenticated, tenant, orgFilterList]: [
          boolean,
          TenantInterface,
          OrganizationFilter[],
        ]) => {
          if (authenticated || !tenant || !orgFilterList.length) return;
          const isTenantChanged = orgFilterList.some(
            item => item.tenantId !== tenant.id
          );

          if (isTenantChanged) {
            this.orgFilterList = [];
            this.orgFilterList$.next([]);
          }
        }
      );
  }

  listenToCentralConfig() {
    this.centralConfig.innovationSpace$.pipe(filter(Boolean)).subscribe(() => {
      const themeWrapper = document.querySelector('.theme-wrapper');
      if (themeWrapper) {
        themeWrapper.classList.add('has-active-innovation-space');
      }
    });
  }

  async toggleCollapse(): Promise<void> {
    this.userPreferences.expanded = !this.userPreferences.expanded;

    this.updateBodyClass();

    if (!this.userPreferences.expanded) {
      this.cancelOrderMode();
    }

    if (this.smallScreen) return;

    await this.userConfig.set(
      StorageEnum.communitySelectorPreferences,
      structuredClone(this.userPreferences)
    );
  }

  toggleFavoriteSection(): void {
    if (this.orderModeActivated) return;

    this.favoriteOpened = !this.favoriteOpened;
  }

  async togglePopularSection(): Promise<void> {
    this.userPreferences.popularOpened = !this.userPreferences.popularOpened;

    if (this.smallScreen) return;

    await this.userConfig.set(
      StorageEnum.communitySelectorPreferences,
      structuredClone(this.userPreferences)
    );
  }

  listenToSearch() {
    this.communitySelectorStored.keyword.subscribe(keyword => {
      this.searchKeyword = keyword;
    });
    this.communitySelectorStored.search$.subscribe(() => {
      void this.search();
    });
  }

  async loadPopularOrg(
    ignoreOrgIds: number[] = this.ignoreOrgIds
  ): Promise<void> {
    try {
      const orgs = await firstValueFrom(
        this.communitySelectorStored.getPopularOrg(ignoreOrgIds)
      );

      this.popularOrgs = orgs;
      this.communitySelectorStored.setPopularOrgs(orgs);
      this.popularOrgs$.next(orgs);
    } catch (error) {
      console.error(error);
    }
  }

  shouldShowAddToFavorite(item): boolean {
    return (
      !this.ignoreOrgIds.includes(item.id) &&
      this.popularOrgs.map(o => o.id).includes(item.id)
    );
  }

  openAddFavoriteDialog() {
    this.modalService.open(this.modalRef, {
      centered: true,
      backdrop: 'static',
      size: 'lg',
      container: 'body',
      modalDialogClass: 'add-to-favorite-modal',
    });
  }

  selectItem(event) {
    this.handleAddOrgToFavorite(event.id, true);
  }

  handleAddOrgToFavorite(orgId: number, showToastMessageOnSuccess = false) {
    if (orgId > 0) {
      const popularOrgIds = this.popularOrgs.map(org => org.id);
      const isPopularOrganization = popularOrgIds.includes(orgId);

      this.storeNewFavoriteOrg(orgId, isPopularOrganization);

      if (showToastMessageOnSuccess) {
        this.toastService.removeAll();
        this.toastService.showSuccess('Sidebar.AddedToMySpaces');
      }
    }
  }

  storeNewFavoriteOrg(orgId, isPopularOrg = false) {
    const challengeService = this.injector.get(ChallengeHttpService);
    const orgService = this.injector.get(OrganizationHttpService);

    const handleReadError = (err: HttpErrorResponse) => {
      if (err.status === HttpStatusCodeEnum.NotFound) {
        return challengeService.read(orgId);
      }
      return throwError(() => new Error(err.message));
    };

    const headers = new HttpHeaders().set(SKIP_INNOVATION_SPACE, 'true');

    this.sessionService.apiReady$
      .pipe(untilDestroyed(this))
      .subscribe(apiReady => {
        if (apiReady) {
          orgService
            .read(orgId, headers)
            .pipe(catchError(handleReadError), untilDestroyed(this))
            .subscribe({
              next: (org: OrganizationInterface) => {
                if (org) {
                  const updatedOrg: OrganizationFilter = {
                    id: org.id,
                    name: org.orgName,
                    imgUrl: org.logo?.url || org.logo,
                    isChallenge: org.showAsChallenge,
                    tenantId: +this.sessionService.getTenantId(),
                    isPopular: isPopularOrg,
                    slug: org.slug,
                    subdomain: org.subdomain,
                  };

                  this.orgFilterList.push(updatedOrg);

                  if (isPopularOrg) {
                    this.popularOrgs = this.popularOrgs.filter(
                      org => org.id !== updatedOrg.id
                    );

                    void this.loadPopularOrg();
                  }

                  void this.setFilterTokenStorage();
                }
              },
              error: err => {
                console.log(err);
              },
            });
        }
      });
  }

  clickItem(id: string, $event?: Event): void {
    $event?.preventDefault();

    if (this.orderModeActivated) return;

    super.onClickItem(id);
  }

  listenToResetInnovationSpace(): void {
    this.communitySelectorStored.innovationSpaceReset$.subscribe(() => {
      this.clickItem('box-all-innovation');
    });
  }

  updateBodyClass(): void {
    const className = 'community-selector-collapse';

    if (this.expanded) {
      document.body.classList.remove(className);
    } else {
      document.body.classList.add(className);
    }
  }

  onDrop(e: CdkDragDrop<OrganizationFilter>) {
    if (this.smallScreen && !this.orderModeActivated) return;
    moveItemInArray(this.orgFilterList, e.previousIndex, e.currentIndex);
  }

  cancelOrderMode(): void {
    if (this.originalOrgList.length > 0) {
      this.orgFilterList = structuredClone(this.originalOrgList);
    }

    setTimeout(() => (this.orderModeActivated = false));
  }

  async onSaveOrgOrder(): Promise<void> {
    this.savingOrgOrder = true;

    await this.setFilterTokenStorage();

    this.originalOrgList = structuredClone(this.orgFilterList);

    this.savingOrgOrder = false;

    this.orderModeActivated = false;
  }

  onOrganizationItemClick(item: OrganizationFilter): void {
    if (this.orderModeActivated) return;

    if (this.organizationId === item.id) return;

    this.selectOrganization(item);
  }

  selectOrganization(item: OrganizationFilter): void {
    this.orgSelected.emit();

    if (
      environment.subdomainOrgFilter?.enabled &&
      !this.centralConfig.innovationSpaceId
    ) {
      if (item.subdomain) {
        this.navigateToOrgSubdomain(item.subdomain);
        return;
      }

      if (SubdomainOrgFilterMarker.lastAppliedSubdomain) {
        this.navigateToBaseDomainWithFilterToken(item.slug || item.id);
        return;
      }
    }

    const spaceId = item.slug || item.id;

    if (this.sessionService.isOnStartPage) {
      this.activateSpaceOnStartPage(item.id, spaceId);
      return;
    }

    this.navigateToSpaceStartPage(spaceId);
  }

  resetInnovationSpaceOnHomePage(): void {
    const currentLocale = this.i18Service.languagePath;

    const isHomePage =
      globalThis.location.pathname ===
      `${currentLocale}${environment.jipUrl.home}`;

    if (!isHomePage) return;

    this.filterStoredService.filterReady$
      .pipe(filter(Boolean), take(1), untilDestroyed(this))
      .subscribe(() => {
        this.clickItem('box-all-innovation');
        StringUtils.removeParamFromUrl(UrlParam.FilterTokenId);
      });
  }

  private activateSpaceOnStartPage(
    orgId: number,
    spaceId: string | number
  ): void {
    StringUtils.setQueryParamOnUrl(
      UrlParam.FilterTokenId,
      `${spaceId}`,
      null,
      true
    );

    void this.filterOrganizations(orgId);
  }

  private navigateToOrgSubdomain(subdomain: string): void {
    const path = this.sessionService.appendLanguagePath(
      environment.jipUrl.startPage
    );
    const url = SubdomainUrlUtils.resolveOrgSubdomainUrl(
      subdomain,
      path,
      !!this.centralConfig.innovationSpaceId
    );

    if (url) this.redirectTo(url);
  }

  private navigateToBaseDomainWithFilterToken(spaceId: string | number): void {
    const { baseDomain } = environment.subdomainOrgFilter;
    const path = this.sessionService.appendLanguagePath(
      environment.jipUrl.startPage
    );
    const protocol = globalThis.location.protocol;

    this.redirectTo(
      `${protocol}//${baseDomain}${path}?${UrlParam.FilterTokenId}=${spaceId}`
    );
  }

  redirectTo(url: string): void {
    globalThis.location.href = url;
  }

  private navigateToSpaceStartPage(spaceId: string | number): void {
    const url =
      environment.jipUrl.startPage + `?${UrlParam.FilterTokenId}=${spaceId}`;

    FormUtils.navigateToWithLanguagePath(this.sessionService, url);
  }

  private withSmallScreen(): void {
    this.smallScreen = window.innerWidth <= MediaBreakpoint.xl;
  }

  private handleSwipe(
    event: HammerInput,
    id: number,
    direction: 'left' | 'right'
  ): void {
    const translateX = event.deltaX;
    this.currentSwipeElement = event.target.closest('.swipe-item');

    const limitedX =
      direction === 'left'
        ? Math.max(translateX, -40)
        : Math.min(translateX, 56);

    this.currentSwipeElement.classList.add('swiping', `swiping-${direction}`);

    if (
      this.mobileRemoveBtnDisplayed &&
      this.activeSwipeItemId === id &&
      this.activeSwipeDirection !== direction
    ) {
      this.resetElementPosition(this.currentSwipeElement);
      this.mobileRemoveBtnDisplayed = false;
    } else {
      this.currentSwipeElement.style.transform = `translateX(${limitedX}px)`;
      this.currentSwipeElement.style.width =
        (direction === 'left' && translateX < 0) ||
        (direction === 'right' && translateX > 0)
          ? `calc(100% - 1rem)`
          : '100%';

      this.mobileRemoveBtnDisplayed = true;
      this.activeSwipeItemId = id;
      this.activeSwipeDirection = direction;
    }

    for (const el of this.swipeableOrgItems()) {
      if (el.nativeElement !== this.currentSwipeElement) {
        this.resetElementPosition(el.nativeElement);
      }
    }
  }

  private resetElementPosition(el: HTMLElement): void {
    el.classList.remove('swiping', 'swiping-left', 'swiping-right');
    el.style.transform = '';
    el.style.width = '';
  }

  private async attachSwipeHandlers(
    items: readonly ElementRef[]
  ): Promise<void> {
    // Hammer is only needed for touch swiping: load it on demand.
    const { default: Hammer } = await import('hammerjs');

    for (const { nativeElement } of items) {
      const hammerTime = new Hammer(nativeElement, {});

      hammerTime.on('swipeleft', e =>
        this.handleSwipe(e, nativeElement.id, 'left')
      );
      hammerTime.on('swiperight', e =>
        this.handleSwipe(e, nativeElement.id, 'right')
      );
    }
  }
}
